import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import { setFinanceInfo, setFinanceQuoteId, startFinanceFetch } from "../redux/actions/UscFinanceActions";
import { enableUscMonthlyRate } from "../utils/settings";
import { getFinanceQuoteFromStorage, getSessionStorageParams } from "../utils/sessionStorage";
import { formatUscFinanceInfo } from "../../shared-logic/utils/uscUtils";
import type { UscDispatchType } from "../redux/store";
import { useUscSelector } from "../redux/store";
import { UscFinanceDataSource } from "../utils/constants";
import { useDispatch } from "../../../shared-logic/redux/store";

const useUscFinanceInit = (): void => {
    const dispatch = useDispatch<UscDispatchType>();
    const vehicleState = useUscSelector((state) => state.vehicle);
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const sessionStorageParams = useUscSelector(getSessionStorageParams, shallowEqual);
    const financeEnabled = useUscSelector(({ commonSettings, uscSettings: uscSettingsState, vehicle }) =>
        enableUscMonthlyRate(commonSettings, uscSettingsState, vehicle.result, vehicle.type),
    );
    const financeQuoteId = useUscSelector((state) => state.finance.financeQuoteId);
    const commonSettings = useUscSelector((state) => state.commonSettings);

    const vehicleLoaded = !!vehicleState.result;
    useEffect(() => {
        if (!vehicleLoaded || !financeEnabled) return;

        const financeSessionQuote = getFinanceQuoteFromStorage(sessionStorageParams, vehicleState.result!.id);
        if (!financeQuoteId) {
            // No quote id in state, check if there is one in session.
            if (financeSessionQuote?.financeQuoteId) dispatch(setFinanceQuoteId(financeSessionQuote.financeQuoteId));
        }

        // Initiate finance info, but only do this when externalPrice is not active.
        // If externalPrice is enabled, externalPrice logic will take care of loading finance info as the finance price depends on it.
        if (!uscSettings.enableExternalPrice) {
            if (!financeQuoteId && !financeSessionQuote?.financeQuoteId) {
                const monthlyPrice = vehicleState.result!.financingOffers
                    ? formatUscFinanceInfo(
                          commonSettings.country,
                          commonSettings.language,
                          vehicleState.result!.financingOffers,
                      ).monthlyPrice
                    : 0;
                // No custom finance info shown, use default finance quote from vehicle.
                dispatch(
                    setFinanceInfo(vehicleState.result!.financeInfo, monthlyPrice, null, {
                        source: UscFinanceDataSource.Df,
                        data: vehicleState.result?.financingOffers?.[0],
                    }),
                );
            } else if (financeQuoteId || financeSessionQuote?.financeQuoteId) {
                // If a financeQuoteId is present, load finance data.
                // Specifically dont fetch data if external prices are shown as the external price logic will take care of fetching finance info.
                dispatch(startFinanceFetch());
            }
        }
    }, [vehicleLoaded]);
};

export default useUscFinanceInit;
